import {
    PREF_KEY_DISPLAY_WELCOME,
    PREF_KEY_LOG_REDACTION_KEYS,
    PREF_KEY_NOTIFICATION_EMAIL,
    PREF_KEY_PRESERVE_DATA_ON_UNINSTALL,
    PREF_KEY_RECEIVE_PRODUCT_UPDATES
} from "./constants";
import type {LogSourceGroup} from "./log-source";

export enum DeliverySource {
    Webhook = 'webhook',
    Poll = 'poll',
    Backfill = 'backfill',
    External = 'external',
    Schedule = 'schedule',
    // An explicit, user-initiated re-delivery of a past log (the /webhook/rerun route). The merchant
    // is deliberately asking the hook to run again, so — like External — it bypasses the reconcile
    // gate's cross-path dedup (otherwise a reliable hook's replay would collide with its committed
    // receipt and be silently skipped).
    Replay = 'replay',
    // The gap events enqueued by the one-shot legacy-poller cutover (src/scripts/migrate-legacy-pollers.ts). The hook
    // is still Instant when they are claimed, so the reconcile gate must NOT drop them as stale Poll/Backfill jobs;
    // they bypass the gate and are deduped on logs._id (their logId is the canonical one every other path uses).
    Migration = 'migration',
    // A "Run a test" run from the hook editor (the Tester). It exists only in that run's context.delivery: a test run
    // is never queued or logged, so no job, log or receipt carries it.
    Test = 'test',
}

export enum AuthenticationMethod {
    BEARER = "bearer",
    API_KEY = "apiKey",
    BASIC = "basic",
    HMAC = "hmac"
}

export enum ApiKeyParameterType {
    HTTP_HEADER = 'httpHeader',
    QUERY_PARAMETER = 'queryParameter',
}

export enum HmacAlgorithm {
    SHA_1 = 'sha1',
    SHA_256 = 'sha256',
    SHA_512 = 'sha512',
    MD5 = 'md5'
}

export enum HmacEncoding {
    BASE64_URL = 'base64url',
    BASE64 = 'base64',
    HEX = 'hex'
}

export type ApiKeySettings = {
    parameterType: ApiKeyParameterType,
    parameterName: string
};

export type BasicSettings = {
    userName: string,
    password: string
};

export type HmacSettings = {
    algorithm: HmacAlgorithm,
    encoding: HmacEncoding,
    signatureHeaderName: string,
    webhookSigningSecret: string
};

export interface Authentication {
    method: AuthenticationMethod,
    apiKeySettings?: ApiKeySettings,
    basicSettings?: BasicSettings,
    hmacSettings?: HmacSettings,
    samplePayload?: any
}

export interface ExtensionPreferences {
    failedHookNotifications?: boolean
    // Outbound rate-limit handling (CLOUDHOOKS-435, R2/R4/R8). Both default undefined = off. The
    // idempotency opt-in has no effect unless automation is on (enforced at read in the worker, U6).
    autoRateLimitHandling?: boolean
    idempotentSafeToRerun?: boolean
}

export interface ExtensionNotifications {
    failedHookPausedUntil?: Date
}

export interface ExtensionVersion {
    name: string;
    code: string;
    savedAt: Date;
}

export interface Extension {
    _id?: string,
    name: string,
    code: string,
    event: string,
    source: string,
    webhookId: string,
    webhookSlug: string,
    shopUrl: string,
    isEnabled: boolean,
    polling?: {
        lastCheckedAt: Date,
        boundaryIds?: string[],
        customEventVerb?: string
    },
    testing?: {
        lastTestedAt: Date,
        failedTestCount: number
    }
    authentication?: Authentication|null
    preferences?: ExtensionPreferences
    notifications?: ExtensionNotifications
    versions?: ExtensionVersion[];
    environmentVariables?: EnvironmentVariable[];
}

export const ACCESS_TOKEN_KEY_HASHED_TOKEN = 'hashedToken'

export interface AccessToken {
    _id?: string,
    name: string,
    [ACCESS_TOKEN_KEY_HASHED_TOKEN]?: string,
    reminder: string,
}

export interface AccessTokenGenerateResult {
    success: boolean,
    error?: string,
    accessToken?: AccessToken
    rawToken?: string
}

export interface AccessTokenDeleteResult {
    success: boolean,
    error?: string,
}

export interface ReAuthorizeResult {
    success: boolean,
    authorizationUrl?: string,
    error?: string,
}

export interface UpgradePlanResult {
    success: boolean,
    authorizationNeeded?: boolean,
    authorizationUrl?: string,
    error?: string,
}

export interface DowngradePlanResult {
    success: boolean,
    error?: string,
}

export interface UsageResult {
    usage: Usage[],
    spendingInCents: number
}

export type PreferencesClientKey =
    | typeof PREF_KEY_DISPLAY_WELCOME
    | typeof PREF_KEY_PRESERVE_DATA_ON_UNINSTALL
    | typeof PREF_KEY_NOTIFICATION_EMAIL
    | typeof PREF_KEY_RECEIVE_PRODUCT_UPDATES
    | typeof PREF_KEY_LOG_REDACTION_KEYS;

export interface PreferencesUpdateError {
    key: PreferencesClientKey,
    error: string,
}

export interface PreferencesUpdateResult {
    success: boolean,
    validationErrors?: PreferencesUpdateError[],
    globalErrors?: string[],
}

export enum LogEventType {
    LOG = "log",
    ERROR = "error",
    ACTION = "action",
}

export interface LogFilter {
    page: any;
    resourceId: any;
    logStatus: any;
    dateFrom?: number;
    dateTo?: number;
}

export enum LogFilterStatus {
    STATUS_ALL = 'STATUS_ALL',
    STATUS_SUCCEEDED = 'STATUS_SUCCEEDED',
    STATUS_FAILED = 'STATUS_FAILED',
    STATUS_UNPROCESSED = 'STATUS_UNPROCESSED',
    // A sleeping re-enqueued run (CLOUDHOOKS-435, KTD-13/R13): derived, not a stored LogStatus —
    // logs.status=PENDING AND retryAfterCount>0 AND notBefore>now.
    STATUS_SCHEDULED = 'STATUS_SCHEDULED',
}

// Bulk replay batch lifecycle (CLOUDHOOKS-458). Persisted on ReplayBatch.state, so the values must stay
// stable. Only Active is open; every other state is terminal and unsets ReplayBatch.isOpen (KTD-3).
export enum ReplayBatchState {
    Active = 'active',
    Completed = 'completed',
    Cancelled = 'cancelled',
    Stopped = 'stopped',
}

// Why the dispatcher stopped a batch (R11, KTD-8). Persisted on ReplayBatch.stopReason.
export enum ReplayBatchStopReason {
    ActionLimit = 'actionLimit',
    FeatureLost = 'featureLost',
    AccountGone = 'accountGone',
    HookDisabled = 'hookDisabled',
    HookDeleted = 'hookDeleted',
}

// Why the dispatcher skipped one batch item at dispatch time (KTD-8). The keys of ReplayBatch.skipped.
export enum ReplayBatchSkipReason {
    NotFinished = 'notFinished',
    NoData = 'noData',
    Expired = 'expired',
}

// The filter scope a batch was created with — the merchant-chosen part of the server-applied log-list
// query (shopUrl and extensionId live on the batch itself).
export interface ReplayBatchScope {
    resourceId: string | null;
    dateFrom: number;
    dateTo: number;
    logStatus: LogFilterStatus;
    logSource: LogSourceGroup;
}

// Outcome counts of a batch's replay logs; kept incrementally while its runs finish, frozen into finalCounts once
// they all have (KTD-16).
export interface ReplayBatchOutcomeCounts {
    succeeded: number;
    failed: number;
    retrying: number;
    running: number;
}

export type ReplayBatchSkippedCounts = Partial<Record<ReplayBatchSkipReason, number>>;

// GET /extensions/:id/replay-preview (CLOUDHOOKS-458, U2): what a bulk replay over the current filters would do.
// Counts are bounded (countDocuments limit), so a value past REPLAY_BATCH_MAX_RUNS means "more than the cap".
export interface ReplayPreview {
    scope: ReplayBatchScope;           // the server-applied scope (R6), not the raw inputs
    replayable: number;                // finished runs with event data
    overCap: boolean;                  // replayable > REPLAY_BATCH_MAX_RUNS: confirm is disabled (R4)
    skipped: ReplayBatchSkippedCounts; // in-scope runs that will not replay, by reason (R2)
    // Skip reasons whose count hit its time bound (maxTimeMS) and is left out of `skipped`: shown as "count
    // unavailable". Empty when every count finished; absent from responses that predate the field.
    skippedUnavailable?: ReplayBatchSkipReason[];
    idempotentSafeToRerun: boolean;    // the hook's "safe to re-run" opt-in, shown in the modal (R5)
}

// Why GET /extensions/:id/replay-preview could not answer (the 400/503 body reason).
export enum ReplayPreviewFailureReason {
    Timeout = 'timeout',                   // the replayable count hit its time bound (maxTimeMS), 503
    MissingDateRange = 'missingDateRange', // no complete date range (both ends set, dateFrom < dateTo), 400
}

// The 400/503 body of GET /extensions/:id/replay-preview.
export interface ReplayPreviewFailure {
    reason: ReplayPreviewFailureReason;
}

// The client-facing view of a ReplayBatch (status endpoint, progress banner).
export interface ReplayBatchStatus {
    id: string;
    state: ReplayBatchState;
    total: number;
    dispatched: number;
    skipped: ReplayBatchSkippedCounts;
    counts: ReplayBatchOutcomeCounts;
    scope: ReplayBatchScope;
    startedAt: Date;
    endedAt?: Date;
    stopReason?: ReplayBatchStopReason;
    // When the batch's runs had all finished and its counts were frozen (KTD-16); absent until then.
    settledAt?: Date;
}

// Why POST /extensions/:id/replay-batches refused to create a batch (CLOUDHOOKS-458, U3). Sent as the reason of
// the 400/409/422 body and logged on replay_batch.create_rejected (FeatureUnavailable answers 403 with text, like
// the single rerun route, and is only logged).
export enum ReplayBatchCreateRejectReason {
    FeatureUnavailable = 'featureUnavailable',
    NotAcknowledged = 'notAcknowledged',
    StatusNotReplayable = 'statusNotReplayable', // the filter status cannot hold finished runs (KTD-13)
    OverCap = 'overCap',                         // more than REPLAY_BATCH_MAX_RUNS replayable runs (R4)
    NothingToReplay = 'nothingToReplay',
    BatchAlreadyOpen = 'batchAlreadyOpen',       // the hook already has an open batch (R7, AE3)
    Timeout = 'timeout',                         // resolving the replayable runs hit its time bound (503)
    MissingDateRange = 'missingDateRange',       // no complete date range (both ends set, dateFrom < dateTo), 400
}

// POST /extensions/:id/replay-batches body: the Logs-tab filters (same meaning as the list route's query) plus the
// confirm modal's acknowledgement (R5). dateFrom/dateTo are epoch ms and required
// (dateFrom < dateTo): a batch always has a date range.
export interface ReplayBatchCreateRequest {
    resourceId?: string | null;
    dateFrom?: number;
    dateTo?: number;
    logStatus?: LogFilterStatus;
    logSource?: LogSourceGroup;
    acknowledged: boolean;
}

// The body of a refused create. On 409 (BatchAlreadyOpen) `batch` is the open batch, so the client can show its
// progress instead (AE3).
export interface ReplayBatchCreateRejection {
    reason: ReplayBatchCreateRejectReason;
    batch?: ReplayBatchStatus | null;
}

export enum LogStatus {
    PENDING = 'pending',       // Ready to be processed
    PROCESSING = 'processing', // Currently being handled
    PROCESSED = 'processed',   // Successfully finished
    UNPROCESSED = 'unprocessed' // Terminal failure state (exceeded retries)
}

export interface LogEvent {
    type: LogEventType;
    args: string[];
}

export const LOG_KEY_ID = '_id'
export const LOG_KEY_EXPIRES_AT = 'expiresAt'

export interface Log {
    [LOG_KEY_ID]?: string;
    shopUrl: string;
    webhookSlug?: string;
    payload: any;
    extensionId: string;
    createdAt: Date;
    events: LogEvent[];
    actionCount: number;
    resourceId: string;
    [LOG_KEY_EXPIRES_AT]?: Date;
    status?: LogStatus;
    attemptCount?: number;
    lastAttemptAt?: Date;
    // Denormalized from LogState by reEnqueueLogState (CLOUDHOOKS-435, KTD-13) so the run-log surface
    // can render a sleeping re-enqueued run as "Scheduled" (status=PENDING AND retryAfterCount>0 AND
    // notBefore>now) without reading LogState. Written only in the post-flip projection, never ahead
    // of the authoritative state flip.
    notBefore?: Date;
    retryAfterCount?: number;
    // Delivery source and replay lineage (CLOUDHOOKS-458, KTD-2). All optional. On master deliverySource is written
    // for replays (Replay) and API calls (External); a missing one is a webhook run, as 432 reads it (R14).
    deliverySource?: DeliverySource;
    // On a replay log: the log it replays (may itself be a replay). Copied from the job at claim.
    replaySourceLogId?: string;
    // On a bulk replay's log: its batch. Copied from the job at claim, so a sleeping retry can tell at wake whether
    // its batch was cancelled or stopped meanwhile (CLOUDHOOKS-458).
    replayBatchId?: string;
}

// GET /extensions/:id/logs: one page of a hook's logs (limit+1 rows, the extra one signals a next page).
export interface LogListResponse {
    logCount: number;              // the hook's unfiltered total: drives the "no logs yet" empty state
    filteredCount?: number;        // runs matching the filters, page 0 only, at most LOG_LIST_COUNT_CAP (KTD-11)
    filteredCountCapped?: boolean; // more than LOG_LIST_COUNT_CAP runs match: shown as "10,000+"
    logs: Log[];
}

export interface Usage {
    _id?: string;
    extensionId: string;
    shopUrl: string;
    allTime: number;

    [key: string]: any;
}

interface PricingRange {
    readonly threshold: number;
    readonly rate: number;
}

export type PricingRanges = readonly PricingRange[];

export enum FeatureKey {
    LogRetentionDays = 'logRetentionDays',
    ExternalTriggers = 'externalTriggers',
    EnvironmentVariables = 'environmentVariables',
    FailedHookNotifications = 'failedHookNotifications',
    HookReplay = 'hookReplay',
    ActionLimit = 'actionLimit',
    // Paid gate for outbound rate-limit handling (CLOUDHOOKS-435, R9/F97): true for Trial/Pro/Custom,
    // false for Free. Without the tier-map entries hasFeature returns false for every plan and the
    // feature is dead on arrival.
    RateLimitHandling = 'rateLimitHandling'
}

export interface Features {
    [FeatureKey.LogRetentionDays]: number;
    [FeatureKey.ExternalTriggers]: boolean;
    [FeatureKey.EnvironmentVariables]: boolean;
    [FeatureKey.FailedHookNotifications]: boolean;
    [FeatureKey.HookReplay]: boolean;
    [FeatureKey.ActionLimit]: number;
    [FeatureKey.RateLimitHandling]: boolean;
}

export enum PlanIds {
    Trial = 'trial',
    Free = 'free',      // Starter
    Pro = 'pro',        // Pro
    Custom = 'custom',  // Pro with custom settings
}

export interface Plan {
    id: PlanIds,
    name: string,
    description?: string,
    price?: PricingRanges,
    features?: Features
}

export enum UserRole {
    ADMIN = 'admin',
    VIEWER = 'viewer',
}

export interface RefreshToken {
    token: string;
    createdAt: Date;
}

export interface User {
    _id?: string;
    email: string;
    role: UserRole;
    active: boolean;
    lastLogin: Date|null;
    password?: string;
    salt?: string;
    refreshTokens?: RefreshToken[];
}

export interface EnvironmentVariable {
    _id?: string;
    key: string;
    value: string;
    isEditing: boolean;
    originalKey?: string;
    originalValue?: string;
    isNew?: boolean;
    isDeleted?: boolean;
}